import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  HStack,
  Input,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import axiosInstance from '../../../api/AxiosInstance';
import FormularioArtesano from './FormularioArtesano';

const TablaArtesanos: React.FC = () => {
  const { isOpen: isOpenVer, onOpen: onOpenVer, onClose: onCloseVer } = useDisclosure();
  const { isOpen: isOpenEditar, onOpen: onOpenEditar, onClose: onCloseEditar } = useDisclosure();
  const { isOpen: isOpenNuevo, onOpen: onOpenNuevo, onClose: onCloseNuevo } = useDisclosure();

  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [data, setData] = useState<any[]>([]);
  const [cargando, setCargando] = useState(false);
  const [busqueda, setBusqueda] = useState('');

  const fetchData = async () => {
    setCargando(true);
    try {
      const res = await axiosInstance.get('/turismo/artesanos');
      setData(res.data || []);
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const datosFiltrados = data.filter((item) =>
    busqueda
      ? `${item.nombres} ${item.apellidos}`.toLowerCase().includes(busqueda.toLowerCase()) ||
        item.tipoArtesania?.toLowerCase().includes(busqueda.toLowerCase())
      : true
  );

  const handleVer = (item: any) => { setSelectedItem(item); onOpenVer(); };
  const handleEditar = (item: any) => { setSelectedItem(item); onOpenEditar(); };
  const handleGuardado = () => { onCloseNuevo(); onCloseEditar(); fetchData(); };
  const handleEliminar = async (item: any) => {
    const ok = window.confirm(`Eliminar el artesano ${item.nombres} ${item.apellidos}?`);
    if (!ok) return;
    try {
      await axiosInstance.delete(`/turismo/artesanos/${item.id}`);
      fetchData();
    } catch {
      // noop
    }
  };

  const handlePdf = (id: number) => {
    const base = axiosInstance.defaults.baseURL || '';
    window.open(`${base}/turismo/artesanos/${id}/pdf`, '_blank');
  };

  const handleExportExcel = () => {
    const rows = [
      ['Nombre', 'Apellidos', 'Teléfono', 'Correo', 'Tipo artesanía', 'Técnica', 'Municipio', 'Localidad', 'Experiencia (años)', 'Estatus'],
      ...datosFiltrados.map((a) => [
        a.nombres || '',
        a.apellidos || '',
        a.telefono || '',
        a.correo || '',
        a.tipoArtesania || '',
        a.tecnica || '',
        a.municipio || '',
        a.localidad || '',
        a.experienciaAnios ?? '',
        a.estatus || '',
      ]),
    ];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Artesanos');
    XLSX.writeFile(wb, `Artesanos_${new Date().toLocaleDateString('es-MX')}.xlsx`);
  };

  const handleExportListaPdf = () => {
    const base = axiosInstance.defaults.baseURL || '';
    window.open(`${base}/turismo/artesanos/export?format=pdf`, '_blank');
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input
            placeholder="Buscar por nombre o artesania..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            size="sm"
            w={{ base: 'full', md: '260px' }}
          />
        </HStack>
        <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>
          Excel
        </Button>
        <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="red" variant="outline" onClick={handleExportListaPdf}>
          PDF
        </Button>
        <Button size="sm" leftIcon={<AddIcon />} bg="#5b182b" color="white" _hover={{ bg: '#691f30' }} onClick={onOpenNuevo}>
          Nuevo artesano
        </Button>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg="#5b182b">
            <Tr>
              <Th color="white" fontSize="xs">Nombre</Th>
              <Th color="white" fontSize="xs">Telefono</Th>
              <Th color="white" fontSize="xs">Tipo artesania</Th>
              <Th color="white" fontSize="xs">Municipio</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={6} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : datosFiltrados.length === 0 ? (
              <Tr><Td colSpan={6} textAlign="center" py={8}><Text color="gray.400">Sin registros</Text></Td></Tr>
            ) : (
              datosFiltrados.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: '#f7efe8' }}>
                  <Td maxW="220px"><Text noOfLines={1} title={`${item.nombres} ${item.apellidos}`}>{item.nombres} {item.apellidos}</Text></Td>
                  <Td>{item.telefono || '-'}</Td>
                  <Td>{item.tipoArtesania || '-'}</Td>
                  <Td>{item.municipio || '-'}</Td>
                  <Td><Badge colorScheme={item.estatus === 'activo' ? 'green' : 'gray'}>{item.estatus}</Badge></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => handleVer(item)}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => handleEditar(item)}>Editar</Button>
                      <Button size="xs" variant="outline" borderColor="#5b182b" color="#5b182b" _hover={{ bg: '#f7efe8' }} onClick={() => handlePdf(item.id)}>PDF</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => handleEliminar(item)}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      <Modal isOpen={isOpenVer} onClose={onCloseVer} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="#f7efe8">Detalle del artesano</ModalHeader>
          <ModalBody py={5} bg="white">
            {selectedItem && <FormularioArtesano modo="view" data={selectedItem} />}
          </ModalBody>
          <ModalFooter borderTop="1px solid" borderColor="gray.100" bg="gray.50">
            <Button onClick={onCloseVer}>Cerrar</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenEditar} onClose={onCloseEditar} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="#f7efe8">Editar artesano</ModalHeader>
          <ModalBody py={5} bg="white">
            {selectedItem && <FormularioArtesano modo="edit" data={selectedItem} onGuardado={handleGuardado} onCancelar={onCloseEditar} />}
          </ModalBody>
        </ModalContent>
      </Modal>

      <Modal isOpen={isOpenNuevo} onClose={onCloseNuevo} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="xl" overflow="hidden">
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color="#5b182b" bg="#f7efe8">Nuevo artesano</ModalHeader>
          <ModalBody py={5} bg="white">
            <FormularioArtesano modo="new" onGuardado={handleGuardado} onCancelar={onCloseNuevo} />
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default TablaArtesanos;

